"use client"

import { useQuery } from "@tanstack/react-query"
import { useContext } from "react"

import { getCourseMaterialChapterProgressOptions } from "@/generated/course-material-api/@tanstack/react-query.generated"
import LoginStateContext from "@/shared-module/common/contexts/LoginStateContext"
import { optionalGeneratedQueryOptions } from "@/utils/optionalGeneratedQueryOptions"

/**
 * Hook to fetch user progress for a specific chapter in a course instance.
 * Returns progress information including score given, score maximum, and exercise completion status.
 * @param courseInstanceId - The course instance ID. Query is disabled if undefined.
 * @param chapterId - The chapter ID to fetch progress for.
 * @returns React Query result containing the user's chapter progress data.
 */
export const useChapterProgress = (courseInstanceId: string | undefined, chapterId: string) => {
  const loginStateContext = useContext(LoginStateContext)

  return useQuery(
    optionalGeneratedQueryOptions({
      value: courseInstanceId,
      enabled: loginStateContext.signedIn === true,
      isReady: (resolvedCourseInstanceId): resolvedCourseInstanceId is string =>
        Boolean(resolvedCourseInstanceId),
      build: (resolvedCourseInstanceId) =>
        getCourseMaterialChapterProgressOptions({
          path: {
            chapter_id: chapterId,
            course_instance_id: resolvedCourseInstanceId,
          },
        }),
    }),
  )
}
